"use client";

import {
  IconCircleCheck,
  IconRobot2,
} from "@central-icons-react/round-filled-radius-3-stroke-2";
import { motion, useReducedMotion } from "framer-motion";
import * as React from "react";

import type { OrgMember } from "@/lib/org/queries";
import type { AgentSummary } from "@/lib/agents/types";
import type { TaskListItem, TaskSnapshot } from "@/lib/tasks/queries";
import { useTaskPatch } from "@/lib/tasks/use-task-patch";
import { cn } from "@/lib/utils";

import { memberLabel } from "./task-assignee-menu";
import { TaskAssigneeMenu } from "./task-assignee-menu";
import { AccountChip, AgentChip } from "./task-field-chips";
import { AccountAvatar, type AccountOption } from "./task-account-menu";
import { TaskChipBadge } from "./task-chip";
import { TaskMoreMenu } from "./task-more-menu";

type Props = {
  task: TaskListItem;
  orgMembers: OrgMember[];
  accounts: AccountOption[];
  agents: AgentSummary[];
  onChanged: (snapshot?: TaskSnapshot) => void;
  onOpenDetail: (taskId: string) => void;
  interactive?: boolean;
  revealAgentWorking?: boolean;
  animateAgentWorkingGrow?: boolean;
  revealReadyForReview?: boolean;
  animateReadyForReviewGrow?: boolean;
  handoffFromAgentWorking?: boolean;
  handoffAlreadyComplete?: boolean;
  onAgentHandoffComplete?: () => void;
  hideAccountPillShadow?: boolean;
  repeatAgentWorkingWave?: boolean;
};

const CARD_PILL_SHADOW = "shadow-[0px_1px_1px_rgba(0,0,0,0.02)]";
const ACCOUNT_PILL_BG = "border-0 bg-[#fafafa]";
const AGENT_WORKING_TEXT = "Agents at work...";
const READY_FOR_REVIEW_TEXT = "Ready for review";
const WAVE_STAGGER_S = 0.042;
const WAVE_CHAR_DURATION_S = 0.52;
const CARD_SECTION_GAP_PX = 16;
const AGENT_WORKING_LINE_PX = 16;
const AGENT_WORKING_WAVE_LIFT_PX = 6;
/** Fixed slot: one line of text plus headroom for the wave lift. */
const AGENT_WORKING_SLOT_PX =
  AGENT_WORKING_LINE_PX + AGENT_WORKING_WAVE_LIFT_PX;
const STATUS_SLOT_PX = AGENT_WORKING_SLOT_PX;
/** Title-to-chips spacing when the status block is fully open (padding + slot + padding). */
const STATUS_BLOCK_PX =
  CARD_SECTION_GAP_PX + STATUS_SLOT_PX + CARD_SECTION_GAP_PX;
const STATUS_SLOT_OPEN_TWEEN = {
  duration: 0.26,
  ease: [0.23, 1, 0.32, 1] as const,
};
const STATUS_SLOT_CLOSE_TWEEN = {
  duration: 0.18,
  ease: [0.23, 1, 0.32, 1] as const,
};
const STATUS_CROSSFADE_OUT_S = 0.18;
const STATUS_CROSSFADE_IN_S = 0.22;
/** Review layer starts before agent layer finishes — reads as an earlier handoff. */
const STATUS_CROSSFADE_IN_LEAD_S = -0.08;
const STATUS_CROSSFADE_BLUR_PX = 2;
const STATUS_EASE = [0.23, 1, 0.32, 1] as const;
const READY_FOR_REVIEW_EASE = [0.23, 1, 0.32, 1] as const;
const READY_FOR_REVIEW_ICON_REVEAL_S = 0.2;
const READY_FOR_REVIEW_TEXT_REVEAL_S = 0.18;
const READY_FOR_REVIEW_TEXT_STAGGER_S = 0.03;

function ReadyForReviewIndicator({
  playReveal = true,
  instant = false,
}: {
  playReveal?: boolean;
  instant?: boolean;
}) {
  const reduceMotion = useReducedMotion();
  const shouldReveal = playReveal && !reduceMotion;

  if (instant || reduceMotion) {
    return (
      <span
        className="inline-flex items-center gap-1 font-caption-1-medium leading-4 text-success-9"
        aria-label={READY_FOR_REVIEW_TEXT}
      >
        <IconCircleCheck className="size-4 shrink-0" aria-hidden />
        {READY_FOR_REVIEW_TEXT}
      </span>
    );
  }

  return (
    <span
      className="inline-flex items-center gap-1 font-caption-1-medium leading-4 text-success-9"
      aria-label={READY_FOR_REVIEW_TEXT}
    >
      <motion.span
        className="inline-flex shrink-0"
        initial={{ scale: 0.92, opacity: 0 }}
        animate={
          shouldReveal ? { scale: 1, opacity: 1 } : { scale: 0.92, opacity: 0 }
        }
        transition={{
          duration: READY_FOR_REVIEW_ICON_REVEAL_S,
          ease: READY_FOR_REVIEW_EASE,
        }}
      >
        <IconCircleCheck className="size-4" aria-hidden />
      </motion.span>
      <motion.span
        className="inline-block"
        initial={{ opacity: 0, transform: "translateY(3px)" }}
        animate={
          shouldReveal
            ? { opacity: 1, transform: "translateY(0px)" }
            : { opacity: 0, transform: "translateY(3px)" }
        }
        transition={{
          duration: READY_FOR_REVIEW_TEXT_REVEAL_S,
          delay: shouldReveal ? READY_FOR_REVIEW_TEXT_STAGGER_S : 0,
          ease: READY_FOR_REVIEW_EASE,
        }}
      >
        {READY_FOR_REVIEW_TEXT}
      </motion.span>
    </span>
  );
}

function AgentWorkingIndicator({
  playWave = true,
  repeat = false,
}: {
  playWave?: boolean;
  repeat?: boolean;
}) {
  const reduceMotion = useReducedMotion();
  const shouldWave = playWave && !reduceMotion;

  return (
    <span
      className="inline-flex items-center gap-1 font-caption-1-medium leading-4 text-[#5db1ed]"
      aria-label={AGENT_WORKING_TEXT}
    >
      <IconRobot2 className="size-4 shrink-0" aria-hidden />
      <span className="inline-flex">
        {AGENT_WORKING_TEXT.split("").map((char, index) => (
          <motion.span
            key={`${char}-${index}`}
            className="inline-block"
            initial={false}
            animate={
              shouldWave
                ? {
                    y: [0, -AGENT_WORKING_WAVE_LIFT_PX, 0],
                    rotate: [0, -6, 0],
                  }
                : { y: 0, rotate: 0 }
            }
            transition={
              shouldWave
                ? {
                    duration: WAVE_CHAR_DURATION_S,
                    delay: index * WAVE_STAGGER_S,
                    ease: [0.34, 1.35, 0.64, 1],
                    times: [0, 0.4, 1],
                    repeat: repeat ? Infinity : 0,
                    repeatDelay: repeat ? 1.15 : 0,
                  }
                : { duration: 0 }
            }
          >
            {char === " " ? "\u00a0" : char}
          </motion.span>
        ))}
      </span>
    </span>
  );
}

function memberInitial(name: string): string {
  return name.slice(0, 1).toUpperCase();
}

function AssigneeAvatar({
  member,
  className,
}: {
  member: OrgMember;
  className?: string;
}) {
  const label = memberLabel(member);
  if (member.avatarUrl) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={member.avatarUrl}
        alt=""
        className={cn(
          "size-5 shrink-0 rounded-full border-[1.25px] border-white object-cover",
          className,
        )}
      />
    );
  }
  return (
    <span
      className={cn(
        "flex size-5 shrink-0 items-center justify-center rounded-full border-[1.25px] border-white bg-neutral-04 text-[10px] font-medium text-neutral-11",
        className,
      )}
    >
      {memberInitial(label)}
    </span>
  );
}

function AssigneeTrigger({
  assignee,
  interactive,
  locked,
  onChange,
  members,
}: {
  assignee: OrgMember | null;
  interactive: boolean;
  locked: boolean;
  onChange: (memberId: string | null) => void;
  members: OrgMember[];
}) {
  const avatar = assignee ? (
    <AssigneeAvatar member={assignee} />
  ) : (
    <span
      className="size-5 shrink-0 rounded-full border border-dashed border-neutral-05"
      aria-hidden
    />
  );

  if (!interactive) {
    return assignee ? (
      <span className="inline-flex shrink-0">{avatar}</span>
    ) : null;
  }

  return (
    <TaskAssigneeMenu
      value={assignee?.id ?? null}
      onChange={onChange}
      members={members}
      disabled={locked}
      trigger={
        <button
          type="button"
          disabled={locked}
          aria-label={
            assignee ? `Assignee: ${memberLabel(assignee)}` : "Set assignee"
          }
          className="inline-flex shrink-0 rounded-full outline-none transition-opacity duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:opacity-80 disabled:opacity-60 [@media(hover:hover)_and_(pointer:fine)]:hover:opacity-80"
        >
          {avatar}
        </button>
      }
    />
  );
}

function stopCardOpen(event: React.SyntheticEvent) {
  event.stopPropagation();
}

export function TaskKanbanCard({
  task,
  orgMembers,
  accounts,
  agents,
  onChanged,
  onOpenDetail,
  interactive = true,
  revealAgentWorking = true,
  animateAgentWorkingGrow = false,
  revealReadyForReview = true,
  animateReadyForReviewGrow = false,
  handoffFromAgentWorking = false,
  handoffAlreadyComplete = false,
  onAgentHandoffComplete,
  hideAccountPillShadow = false,
  repeatAgentWorkingWave = false,
}: Props) {
  const reduceMotion = useReducedMotion();
  const locked = task.lockedByAgent;
  const readyForReview = task.status === "needs_review";
  const showAgentWorking = !animateAgentWorkingGrow || revealAgentWorking;
  const showReadyForReview = !animateReadyForReviewGrow || revealReadyForReview;
  const showAgentContent = locked && showAgentWorking;
  const showReviewContent = readyForReview && showReadyForReview;
  const lockStatusSlotHeight =
    animateAgentWorkingGrow || animateReadyForReviewGrow;
  const [agentFadingOut, setAgentFadingOut] = React.useState(
    handoffFromAgentWorking && !handoffAlreadyComplete,
  );
  const wasShowingAgentRef = React.useRef(
    (showAgentContent || handoffFromAgentWorking) && !handoffAlreadyComplete,
  );
  const hasActiveStatusSpacing =
    showAgentContent || showReviewContent || agentFadingOut;
  const [waveReady, setWaveReady] = React.useState(
    showAgentWorking && (!animateAgentWorkingGrow || !!reduceMotion),
  );
  const [reviewRevealReady, setReviewRevealReady] = React.useState(
    () =>
      showReadyForReview &&
      (!animateReadyForReviewGrow ||
        !!reduceMotion ||
        handoffFromAgentWorking ||
        handoffAlreadyComplete),
  );
  const [statusSlotEverExpanded, setStatusSlotEverExpanded] = React.useState(
    () =>
      handoffAlreadyComplete ||
      handoffFromAgentWorking ||
      (animateAgentWorkingGrow && revealAgentWorking && showAgentContent) ||
      (animateReadyForReviewGrow &&
        revealReadyForReview &&
        readyForReview &&
        showReviewContent),
  );

  React.useEffect(() => {
    if (!showAgentWorking) {
      setWaveReady(false);
      return;
    }
    if (!animateAgentWorkingGrow || reduceMotion) {
      setWaveReady(true);
    }
  }, [animateAgentWorkingGrow, reduceMotion, showAgentWorking]);

  React.useEffect(() => {
    if (handoffAlreadyComplete) {
      wasShowingAgentRef.current = false;
      setAgentFadingOut(false);
      return;
    }
    if (handoffFromAgentWorking) {
      wasShowingAgentRef.current = true;
      setAgentFadingOut(true);
      setWaveReady(true);
    }
  }, [handoffAlreadyComplete, handoffFromAgentWorking]);

  React.useEffect(() => {
    if (handoffAlreadyComplete) return;
    if (showAgentContent) {
      wasShowingAgentRef.current = true;
      setAgentFadingOut(false);
      return;
    }

    if (wasShowingAgentRef.current && readyForReview) {
      setAgentFadingOut(true);
    } else if (!readyForReview) {
      wasShowingAgentRef.current = false;
      setAgentFadingOut(false);
    }
  }, [handoffAlreadyComplete, readyForReview, showAgentContent]);

  React.useEffect(() => {
    if (!showReadyForReview) {
      setReviewRevealReady(false);
      return;
    }
    if (
      !animateReadyForReviewGrow ||
      reduceMotion ||
      agentFadingOut ||
      handoffAlreadyComplete
    ) {
      setReviewRevealReady(true);
    }
  }, [
    agentFadingOut,
    animateReadyForReviewGrow,
    handoffAlreadyComplete,
    reduceMotion,
    showReadyForReview,
  ]);

  const patchTask = useTaskPatch(task.id, { onSuccess: onChanged });
  const assignee =
    orgMembers.find((member) => member.id === task.assignee?.id) ?? null;
  const account = accounts.find((item) => item.id === task.account?.id) ?? null;
  const agent = agents.find((item) => item.id === task.agentId) ?? null;

  const title = <p className="font-body-s-book text-[#1a1a1a]">{task.title}</p>;

  const showAgentLayer = showAgentContent || agentFadingOut;
  const showReviewLayer =
    readyForReview &&
    (showReviewContent || agentFadingOut || handoffAlreadyComplete);
  const reviewHandoffSettled = handoffAlreadyComplete && showReviewContent;
  const effectiveReviewReveal = reviewRevealReady || handoffAlreadyComplete;
  const statusSlotOpen = showAgentLayer || showReviewLayer;
  const displayStatusSlotOpen = lockStatusSlotHeight
    ? statusSlotEverExpanded || statusSlotOpen
    : statusSlotOpen;
  const shouldAnimateStatusSlot =
    !reduceMotion && (animateAgentWorkingGrow || animateReadyForReviewGrow);
  const shouldGrowStatusSlot =
    shouldAnimateStatusSlot && statusSlotOpen && !statusSlotEverExpanded;
  const statusLayoutTransition = !shouldGrowStatusSlot
    ? { duration: 0 }
    : STATUS_SLOT_OPEN_TWEEN;
  const displayStatusSpacing =
    lockStatusSlotHeight && statusSlotEverExpanded
      ? true
      : hasActiveStatusSpacing;

  React.useEffect(() => {
    if (lockStatusSlotHeight && statusSlotOpen && !shouldGrowStatusSlot) {
      setStatusSlotEverExpanded(true);
    }
  }, [lockStatusSlotHeight, shouldGrowStatusSlot, statusSlotOpen]);

  // When the slot mounts already expanded, the grow animation is skipped and
  // its onAnimationComplete never fires — start the wave/reveal here instead.
  React.useEffect(() => {
    if (!statusSlotOpen || shouldGrowStatusSlot) return;
    if (showAgentContent) setWaveReady(true);
    if (showReviewContent) setReviewRevealReady(true);
  }, [shouldGrowStatusSlot, showAgentContent, showReviewContent, statusSlotOpen]);

  React.useEffect(() => {
    if (!lockStatusSlotHeight) return;
    if (
      task.status === "todo" &&
      !showAgentContent &&
      !showReviewContent &&
      !agentFadingOut
    ) {
      setStatusSlotEverExpanded(false);
    }
  }, [
    agentFadingOut,
    lockStatusSlotHeight,
    showAgentContent,
    showReviewContent,
    task.status,
  ]);

  const openDetail = React.useCallback(() => {
    if (!interactive) return;
    onOpenDetail(task.id);
  }, [interactive, onOpenDetail, task.id]);

  return (
    <article
      onClick={openDetail}
      className={cn(
        "box-border flex h-auto w-full max-w-none min-w-0 shrink-0 flex-col overflow-hidden rounded-[8px] border border-[#eee] bg-white shadow-[0px_1px_1px_rgba(0,0,0,0.02)] transition-[background-color] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)]",
        interactive && "cursor-pointer hover:bg-neutral-02",
      )}
    >
      <div className="flex flex-col p-3">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            {interactive ? (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  openDetail();
                }}
                className="w-full text-left outline-none"
              >
                {title}
              </button>
            ) : (
              title
            )}
          </div>
          <div
            className="shrink-0"
            onPointerDown={stopCardOpen}
            onClick={stopCardOpen}
          >
            <AssigneeTrigger
              assignee={assignee}
              interactive={interactive}
              locked={locked}
              onChange={(memberId) =>
                void patchTask({ assigneeUserId: memberId })
              }
              members={orgMembers}
            />
          </div>
        </div>

        {shouldAnimateStatusSlot || displayStatusSlotOpen ? (
          <motion.div
            className="overflow-hidden will-change-[height,opacity]"
            initial={false}
            animate={{
              height: displayStatusSlotOpen ? STATUS_BLOCK_PX : 0,
              opacity: displayStatusSlotOpen ? 1 : 0,
            }}
            transition={{
              height: statusLayoutTransition,
              opacity: statusLayoutTransition,
            }}
            onAnimationComplete={() => {
              if (statusSlotOpen) {
                setStatusSlotEverExpanded(true);
              }
              if (
                showAgentContent &&
                animateAgentWorkingGrow &&
                !reduceMotion
              ) {
                setWaveReady(true);
              }
              if (
                showReviewContent &&
                animateReadyForReviewGrow &&
                !reduceMotion &&
                !reviewRevealReady
              ) {
                setReviewRevealReady(true);
              }
            }}
          >
            {showAgentLayer || showReviewLayer ? (
              <div
                className="box-border flex flex-col justify-end"
                style={{
                  height: STATUS_BLOCK_PX,
                  paddingTop: CARD_SECTION_GAP_PX,
                  paddingBottom: CARD_SECTION_GAP_PX,
                }}
              >
                <div
                  className="relative w-full"
                  style={{ height: STATUS_SLOT_PX }}
                >
                  {showAgentLayer ? (
                    <motion.div
                      className="absolute inset-0 flex items-end"
                      initial={{ opacity: 1, filter: "blur(0px)" }}
                      animate={{
                        opacity: showAgentContent ? 1 : 0,
                        filter: showAgentContent
                          ? "blur(0px)"
                          : `blur(${STATUS_CROSSFADE_BLUR_PX}px)`,
                      }}
                      transition={{
                        duration: STATUS_CROSSFADE_OUT_S,
                        ease: STATUS_EASE,
                      }}
                      onAnimationComplete={() => {
                        if (!showAgentContent && agentFadingOut) {
                          wasShowingAgentRef.current = false;
                          setAgentFadingOut(false);
                          onAgentHandoffComplete?.();
                        }
                      }}
                    >
                      <AgentWorkingIndicator
                        playWave={waveReady}
                        repeat={repeatAgentWorkingWave}
                      />
                    </motion.div>
                  ) : null}
                  {showReviewLayer ? (
                    <motion.div
                      className="absolute inset-0 flex items-center"
                      initial={
                        reviewHandoffSettled
                          ? false
                          : {
                              opacity: 0,
                              filter: `blur(${STATUS_CROSSFADE_BLUR_PX}px)`,
                            }
                      }
                      animate={{
                        opacity:
                          reviewHandoffSettled ||
                          showReviewContent ||
                          agentFadingOut
                            ? 1
                            : 0,
                        filter:
                          reviewHandoffSettled ||
                          showReviewContent ||
                          agentFadingOut
                            ? "blur(0px)"
                            : `blur(${STATUS_CROSSFADE_BLUR_PX}px)`,
                      }}
                      transition={
                        reviewHandoffSettled
                          ? { duration: 0 }
                          : {
                              duration: STATUS_CROSSFADE_IN_S,
                              delay: STATUS_CROSSFADE_IN_LEAD_S,
                              ease: STATUS_EASE,
                            }
                      }
                    >
                      <ReadyForReviewIndicator
                        playReveal={effectiveReviewReveal}
                        instant={reviewHandoffSettled}
                      />
                    </motion.div>
                  ) : null}
                </div>
              </div>
            ) : null}
          </motion.div>
        ) : null}

        <motion.div
          className="flex items-center gap-2"
          initial={false}
          animate={{
            marginTop: displayStatusSpacing ? 0 : CARD_SECTION_GAP_PX,
          }}
          transition={{ marginTop: statusLayoutTransition }}
        >
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            {interactive ? (
              <span onPointerDown={stopCardOpen} onClick={stopCardOpen}>
                <AccountChip
                  value={task.account?.id ?? null}
                  onChange={(accountId) => void patchTask({ accountId })}
                  accounts={accounts}
                  disabled={locked}
                  labelClassName="max-w-[6rem] truncate"
                  className={cn(
                    ACCOUNT_PILL_BG,
                    hideAccountPillShadow ? "shadow-none" : CARD_PILL_SHADOW,
                    account ? "pl-1.5" : "px-[10px]",
                  )}
                  avatarClassName="rounded-full"
                  showEmptyIcon={false}
                />
              </span>
            ) : account ? (
              <TaskChipBadge
                filled
                title={`Account: ${account.name}`}
                className={cn(
                  ACCOUNT_PILL_BG,
                  hideAccountPillShadow ? "shadow-none" : CARD_PILL_SHADOW,
                  "pl-1.5",
                )}
              >
                <AccountAvatar account={account} className="rounded-full" />
                <span className="max-w-[6rem] truncate">{account.name}</span>
              </TaskChipBadge>
            ) : null}

            {!readyForReview &&
              (interactive ? (
                <span onPointerDown={stopCardOpen} onClick={stopCardOpen}>
                  <AgentChip
                    value={task.agentId}
                    onChange={(agentId) => void patchTask({ agentId })}
                    agents={agents}
                    disabled={locked}
                    labelClassName="max-w-[6rem] truncate"
                    className={cn(
                      CARD_PILL_SHADOW,
                      agent ? "px-2" : "px-[10px]",
                    )}
                    showIcon={false}
                  />
                </span>
              ) : agent ? (
                <TaskChipBadge
                  filled
                  title={`Agent: ${agent.name}`}
                  className={cn(CARD_PILL_SHADOW, "px-2")}
                >
                  <span className="max-w-[6rem] truncate">{agent.name}</span>
                </TaskChipBadge>
              ) : null)}
          </div>

          {interactive ? (
            <span onPointerDown={stopCardOpen} onClick={stopCardOpen}>
              <TaskMoreMenu taskId={task.id} onDeleted={onChanged} />
            </span>
          ) : null}
        </motion.div>
      </div>
    </article>
  );
}
